import { Box, Grid, InputAdornment, OutlinedInput, Tab, Tabs, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { Deal, DealType } from "src/business/deal";
import { DealListItem } from "./DealListItem";
import { colorBorder, colorSurface, colorTextMuted, radiusCard } from "src/design/tokens";
import { useTranslation } from "react-i18next";
import { Card } from "src/design";

type TabValue = "all" | "deals" | "events";

interface Props {
  deals: Deal[];
  canUpdate: boolean;
  onToggle: (deal: Deal) => void;
  onEdit: (deal: Deal) => void;
}

export const DealsList = ({ deals, canUpdate, onToggle, onEdit }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabValue>("all");

  const filtered = deals.filter((d) => {
    const matchesSearch = d.content.toLowerCase().includes(search.toLowerCase());
    const matchesTab =
      tab === "all" ||
      (tab === "deals" && d.type === DealType.DEAL) ||
      (tab === "events" && d.type === DealType.EVENT);
    return matchesSearch && matchesTab;
  });

  return (
    <Box>
      <Card>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm>
            <OutlinedInput
              placeholder={t("deals.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colorTextMuted }} />
                </InputAdornment>
              }
              sx={{
                height: 40,
                fontSize: "14px",
                borderRadius: "8px",
                backgroundColor: colorSurface,
                "& fieldset": { borderColor: colorBorder },
              }}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 40,
                "& .MuiTabs-indicator": { backgroundColor: "#111827", height: 2 },
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: colorTextMuted,
                  px: 1.5,
                  "&.Mui-selected": { color: "#111827", fontWeight: 600 },
                },
              }}
            >
              <Tab label={t("deals.filterAll")} value="all" />
              <Tab label={t("deals.filterDeals")} value="deals" />
              <Tab label={t("deals.filterEvents")} value="events" />
            </Tabs>
          </Grid>
        </Grid>
      </Card>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
        {filtered.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: colorSurface,
              border: `1px solid ${colorBorder}`,
              borderRadius: radiusCard,
            }}
          >
            <Typography sx={{ fontSize: "14px", color: colorTextMuted }}>
              {t("deals.empty")}
            </Typography>
          </Box>
        ) : (
          filtered.map((deal) => (
            <DealListItem
              key={deal.id}
              deal={deal}
              canUpdate={canUpdate}
              onToggle={onToggle}
              onEdit={onEdit}
            />
          ))
        )}
      </Box>
    </Box>
  );
};
